import { useState } from "react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

const mcpUrl = `${import.meta.env.VITE_CONVEX_URL?.replace(".cloud", ".site") ?? ""}/mcp`;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="shrink-0 text-xs px-2.5 py-1.5 rounded border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// FR-C05: Available scope domains for API key scoping
const AVAILABLE_SCOPES = [
  { id: "business", label: "Business", description: "Repository, project, and task management" },
  { id: "execution", label: "Execution", description: "Start, complete, and fail task executions" },
  { id: "asset", label: "Asset", description: "Upload, list, and manage assets" },
  { id: "diagnostic", label: "Diagnostic", description: "Ad-hoc queries, health checks, schema introspection" },
] as const;

export function ApiKeysPage() {
  const keys = useQuery(api.apiKeys.listMyKeys, {});
  const createKey = useAction(api.apiKeys.create);
  const revokeKey = useMutation(api.apiKeys.revoke);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const rotateKey = useAction(api.apiKeys.rotate);
  const [creating, setCreating] = useState(false);
  const [rotating, setRotating] = useState<string | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [scopingEnabled, setScopingEnabled] = useState(false);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      // FR-C05: pass scopes if scoping is enabled and at least one scope selected
      const scopes = scopingEnabled && selectedScopes.length > 0 ? selectedScopes : undefined;
      const result = await createKey({ name: newKeyName.trim(), scopes });
      setCreatedKey(result.key);
      setNewKeyName("");
      // Reset scoping form
      setSelectedScopes([]);
      setScopingEnabled(false);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: any) => {
    await revokeKey({ keyId });
  };

  const handleRotate = async (keyId: any) => {
    setRotating(keyId);
    try {
      const result = await rotateKey({ keyId });
      setCreatedKey(result.key);
    } finally {
      setRotating(null);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">API Keys</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Create keys to connect Claude Code via MCP.
          </p>
        </div>
      </div>

      {/* Created key banner */}
      {createdKey && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 space-y-3">
          <p className="text-xs text-green-400 font-medium">Key created — copy it now, it won't be shown again</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-zinc-800 rounded px-2 py-1.5 font-mono text-green-300 break-all select-all">
              {createdKey}
            </code>
            <CopyButton text={createdKey} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[11px] text-zinc-400">Claude Code setup command:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] bg-zinc-800 rounded px-2 py-1.5 font-mono text-zinc-300 break-all select-all">
                {`claude mcp add --transport http -H "X-API-Key: ${createdKey}" task-tracker ${mcpUrl}`}
              </code>
              <CopyButton text={`claude mcp add --transport http -H "X-API-Key: ${createdKey}" task-tracker ${mcpUrl}`} />
            </div>
          </div>
          <button
            onClick={() => setCreatedKey(null)}
            className="text-xs text-zinc-500 hover:text-zinc-300"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create new key */}
      <form onSubmit={(e) => { e.preventDefault(); void handleCreate(); }} className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g. my-laptop)"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-500"
          />
          <button
            type="submit"
            disabled={!newKeyName.trim() || creating || (scopingEnabled && selectedScopes.length === 0)}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white rounded-lg transition-colors"
          >
            {creating ? "..." : "Create key"}
          </button>
        </div>

        {/* FR-C05: Scope selector */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={scopingEnabled}
              onChange={(e) => {
                setScopingEnabled(e.target.checked);
                if (!e.target.checked) setSelectedScopes([]);
              }}
              className="rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
            />
            <span className="text-xs text-zinc-400">Restrict to specific domains</span>
          </label>

          {scopingEnabled && (
            <div className="grid grid-cols-2 gap-2 pl-6">
              {AVAILABLE_SCOPES.map((scope) => (
                <label key={scope.id} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(scope.id)}
                    onChange={(e) => {
                      setSelectedScopes((prev) =>
                        e.target.checked
                          ? [...prev, scope.id]
                          : prev.filter((s) => s !== scope.id)
                      );
                    }}
                    className="mt-0.5 rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-xs text-zinc-300">{scope.label}</span>
                    <p className="text-[10px] text-zinc-600">{scope.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      </form>

      {/* Key list */}
      {keys === undefined ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No API keys yet.</p>
          <p className="text-sm text-zinc-500 mt-1">Create one above to get started.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {keys.map((k) => (
            <div
              key={k.id}
              className="flex items-center justify-between py-3 px-4 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors group"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="text-sm text-zinc-300">{k.name}</span>
                <span className="text-xs font-mono text-zinc-600">{k.prefix}...</span>
                <span className="text-xs text-zinc-600">{k.lastUsedAt ? timeAgo(k.lastUsedAt) : "Never used"}</span>
                {k.expiresAt && (
                  <span className={`text-xs ${k.expiresAt < Date.now() ? "text-red-400" : "text-zinc-600"}`}>
                    {k.expiresAt < Date.now() ? "Expired" : `Expires ${timeAgo(k.expiresAt)}`}
                  </span>
                )}
                {/* FR-C05: Display scope badges */}
                {k.scopes ? (
                  <span className="flex gap-1">
                    {k.scopes.map((s: string) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                        {s}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-[10px] text-zinc-600">unrestricted</span>
                )}
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                <button
                  onClick={() => void handleRotate(k.id)}
                  disabled={rotating === k.id}
                  className="text-xs text-zinc-600 hover:text-indigo-400 transition-colors"
                >
                  {rotating === k.id ? "..." : "Rotate"}
                </button>
                <button
                  onClick={() => void handleRevoke(k.id)}
                  className="text-xs text-zinc-600 hover:text-red-400 transition-colors"
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-zinc-600 leading-relaxed">
        Create a key above, then run the setup command in Claude Code to configure the MCP connection.
      </p>
    </div>
  );
}
